/**
 * Phase 10 R2 — useTicketLaborLines
 *
 * Sell-side ONLY. Reads the 7 labor columns for a ticket and returns
 * per-line labor objects + ticket totals. Never feeds the scheduler /
 * optiTime / pay / cost paths.
 *
 * NOTE: ticket_jobs has a composite primary key (ticket_id, job_id) — there is
 * no surrogate `id` column. Mutations key on (ticket_id, job_id) accordingly.
 *
 * AUTHORITY (FF-A / FF-F):
 *   Reads from `public.v_ticket_jobs_labor` (SECURITY INVOKER view,
 *   migration `20260503120000_ff_f_labor_read_authority.sql`) — NOT
 *   directly from `ticket_jobs`. The view masks the 7 labor financial
 *   columns to NULL (and `labor_rate_overridden` to `false`) when the
 *   parent shop is not entitled
 *   (`tier IN (CATALYST,ENTERPRISE) OR addons @> labor_engine`).
 *   Existing ticket_jobs RLS still applies via SECURITY INVOKER.
 *   For non-entitled shops the existing reducer treats every line as
 *   `is_uncomputed = true` and totals collapse to
 *   `total_labor_sell: 0, computed_count: 0` — composers report
 *   `'insufficient'` accordingly. FF-A's BEFORE INSERT/UPDATE trigger
 *   continues to gate WRITES to the same columns.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TicketLaborLine {
  ticket_id: string;
  job_id: string;
  job_name: string;
  job_category: string | null;
  effective_minutes: number; // minutes_override ?? jobs.standard_minutes
  sold_minutes: number | null;
  labor_rate_applied: number | null;
  labor_rate_resolved: number | null;
  labor_rate_source: string | null;
  labor_rate_reason: string | null;
  labor_sell_amount: number | null;
  labor_rate_overridden: boolean;
  /** True when none of the 7 labor columns have been written yet (besides the boolean default). */
  is_uncomputed: boolean;
}

export interface TicketLaborTotals {
  total_labor_sell: number;
  computed_count: number;
  uncomputed_count: number;
  overridden_count: number;
}

export interface UseTicketLaborLinesResult {
  lines: TicketLaborLine[];
  totals: TicketLaborTotals;
}

export function useTicketLaborLines(ticketId: string | undefined, enabled = true) {
  return useQuery<UseTicketLaborLinesResult>({
    queryKey: ['ticket-labor-lines', ticketId],
    enabled: !!ticketId && enabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_ticket_jobs_labor')
        .select(
          `ticket_id, job_id, minutes_override,
           sold_minutes, labor_rate_applied, labor_rate_resolved,
           labor_rate_source, labor_rate_reason, labor_sell_amount,
           labor_rate_overridden,
           job_canonical_id, job_name, job_standard_minutes, job_category`,
        )
        .eq('ticket_id', ticketId)
        .is('deleted_at', null);
      if (error) throw error;

      const lines: TicketLaborLine[] = (data ?? []).map((r: any) => {
        const effective = r.minutes_override ?? r.job_standard_minutes ?? 0;
        const isUncomputed =
          r.labor_sell_amount == null &&
          r.labor_rate_applied == null &&
          r.sold_minutes == null;
        return {
          ticket_id: r.ticket_id,
          job_id: r.job_id,
          job_name: r.job_name ?? 'Unnamed job',
          job_category: r.job_category ?? null,
          effective_minutes: effective,
          sold_minutes: r.sold_minutes,
          labor_rate_applied:
            r.labor_rate_applied != null ? Number(r.labor_rate_applied) : null,
          labor_rate_resolved:
            r.labor_rate_resolved != null ? Number(r.labor_rate_resolved) : null,
          labor_rate_source: r.labor_rate_source,
          labor_rate_reason: r.labor_rate_reason,
          labor_sell_amount:
            r.labor_sell_amount != null ? Number(r.labor_sell_amount) : null,
          labor_rate_overridden: !!r.labor_rate_overridden,
          is_uncomputed: isUncomputed,
        };
      });

      const totals: TicketLaborTotals = lines.reduce<TicketLaborTotals>(
        (acc, l) => {
          if (l.labor_sell_amount != null) {
            acc.total_labor_sell += l.labor_sell_amount;
            acc.computed_count += 1;
          } else {
            acc.uncomputed_count += 1;
          }
          if (l.labor_rate_overridden) acc.overridden_count += 1;
          return acc;
        },
        { total_labor_sell: 0, computed_count: 0, uncomputed_count: 0, overridden_count: 0 },
      );
      totals.total_labor_sell = Math.round(totals.total_labor_sell * 100) / 100;

      return { lines, totals };
    },
  });
}
